import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { EmptyState } from "../EmptyState";
import { Search, ClipboardList, ChevronRight, Sparkles } from "lucide-react";

const APP_STATUS_CLS = {
  submetida: "bg-slate-100 text-slate-700",
  em_analise: "bg-amber-100 text-amber-700",
  entrevista: "bg-sky-100 text-sky-700",
  aceite: "bg-emerald-100 text-emerald-700",
  rejeitada: "bg-red-100 text-red-700",
};

const APP_STATES = ["submetida", "em_analise", "entrevista", "aceite", "rejeitada"];

export function AdminApplicationsSection({ onOpenJob, onOpenUser }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (status) params.set("status", status);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      const { data } = await api.get(`/admin/applications?${params.toString()}`);
      setRows(data.applications);
      setTotal(data.total);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [search, status, offset]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setOffset(0); }, [search, status]);

  const changeStatus = async (appId, newStatus) => {
    try {
      await api.patch(`/admin/applications/${appId}`, { status: newStatus });
      toast.success("Estado atualizado.");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 data-testid="admin-applications-title" className="font-heading font-black tracking-tighter text-2xl text-slate-900">Candidaturas</h2>
        <p className="text-sm text-slate-500">Vista global de todas as candidaturas na plataforma. Podes filtrar e mudar o estado diretamente.</p>
      </div>

      <div className="bg-white rounded-xl border border-black/5 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="admin-applications-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Procurar por candidato, empresa ou vaga..." className="pl-9" />
        </div>
        <select data-testid="admin-applications-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm text-slate-700">
          <option value="">Todos os estados</option>
          {APP_STATES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <div className="text-xs text-slate-500 tabular-nums">
          {loading ? "A carregar..." : `${total} candidaturas`}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10"><EmptyState icon={ClipboardList} title="Nenhuma candidatura" description="Ajusta os filtros ou a pesquisa." /></div>
        ) : (
          <ul className="divide-y divide-black/5">
            {rows.map((a) => (
              <li key={a.id} data-testid={`admin-app-row-${a.id}`} className="px-5 py-4 hover:bg-slate-50">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => onOpenUser?.(a.candidate_id)} className="font-semibold text-slate-900 hover:text-amber-600">
                        {a.candidate_name || a.candidate_email || "(sem nome)"}
                      </button>
                      <span className="text-slate-300">→</span>
                      <button type="button" onClick={() => onOpenJob?.(a.job_id)} className="font-semibold text-slate-700 hover:text-amber-600">
                        {a.job_title || a.job_id}
                      </button>
                      {a.match_score != null && (
                        <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]"><Sparkles className="w-3 h-3 mr-1" />{a.match_score}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {a.company_name} · {a.created_at ? new Date(a.created_at).toLocaleDateString("pt-PT") : ""}
                      {a.candidate_email && ` · ${a.candidate_email}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select value={a.status} onChange={(e) => changeStatus(a.id, e.target.value)}
                      className={`h-8 rounded-md border border-input bg-white px-2 text-xs font-semibold ${APP_STATUS_CLS[a.status] || ""}`}>
                      {APP_STATES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                    {onOpenJob && (
                      <Button size="sm" variant="outline" className="rounded-full px-2" onClick={() => onOpenJob(a.job_id)}>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{offset + 1}–{Math.min(offset + limit, total)} de {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Anterior</Button>
            <Button size="sm" variant="outline" className="rounded-full" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Seguinte</Button>
          </div>
        </div>
      )}
    </div>
  );
}
